import React, { useEffect, useState } from "react";
import { Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row, Spinner } from "reactstrap";
import { AUTOPILOT_COLORS } from "../../../../../components/autopilot/styles";
import { useToast } from "../../../../../hooks/toast";
import { adicionarMinhaTransportadora } from "../../../../../services/carteiraTransportadoraApi";
import { digitsOnly, isValidCnpj, mascaraDocumentoParcial } from "../../../../../utils/formatDoc";
import { FASE_ROTULO } from "../esteiraEstado";

// "Adicionar transportadora" (PO1-3283, épico PO1-3279): substitui Convidar e
// Conectar na carteira. CNPJ + apelido opcional + contatos (TI obrigatório;
// Logística e Cotação opcionais). O backend decide a fase:
//   201 { estado: 'PEDIDO_EMBARCADOR', idPedido }  → pedido enviado à Everlog (fase 1)
//   200 { estado: 'EM_ONBOARDING' | 'OPERACIONAL' | 'AGUARDANDO_HOMOLOGACAO', ... } (fase 2)
//   202 { estado: 'EM_CURADORIA', curadoria }
// A API atual devolve { modo: 'pedido', pedido } ou { modo: 'onboarding',
// onboarding: { estado, ... } }; `lerResultado` normaliza os dois formatos.
// Nos onChange o valor é lido ANTES do setState (React 16: event pooling).
const MENSAGENS = {
  matriz_inativa: "A matriz (0001) desta transportadora está inativa na Receita. Fale com a Everlog.",
  contato_ti_obrigatorio: "Informe nome e e-mail do contato de TI da transportadora.",
  cnpj_invalido: "Informe um CNPJ válido (14 dígitos).",
  pedido_aberto: "Já existe um pedido ou convite em aberto para esta transportadora.",
  convite_ja_existe: "Já existe um convite em aberto para esta transportadora.",
  transportadora_ja_tem_acesso: "Esta transportadora já está na sua carteira e acessa o portal.",
  vinculo_inexistente: "A transportadora não está na sua carteira.",
  ESCOPO_NEGADO: "Você não tem acesso a este embarcador.",
};

const PAPEIS = [
  { key: "ti", label: "TI (API/SFTP)", obrigatorio: true, ajuda: "Quem recebe o acesso SFTP e o convite do portal." },
  { key: "logistica", label: "Logística (dia a dia)", obrigatorio: false },
  { key: "cotacao", label: "Cotação (comercial)", obrigatorio: false },
];
const CONTATO_VAZIO = { nome: "", email: "", telefone: "" };
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: "0.25rem" };
const labelStyle = { color: AUTOPILOT_COLORS.text };
const smallLabel = { color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem" };

export function lerResultado(status, data) {
  const d = data || {};
  if (d.modo === "pedido" || d.estado === "PEDIDO_EMBARCADOR" || status === 201) {
    return { fase: "pedido", estado: "PEDIDO_EMBARCADOR", idPedido: d.idPedido || (d.pedido && d.pedido.id) || null, homologada: !!d.homologada };
  }
  const o = d.onboarding || d.resultado || d;
  const estado = o.estado || d.estado || null;
  if (status === 202 || estado === "EM_CURADORIA") {
    return { fase: "curadoria", estado: "EM_CURADORIA", curadoria: o.curadoria || d.curadoria || null };
  }
  return { fase: "onboarding", estado, idTransportadora: o.idTransportadora || d.idTransportadora || null, passos: o.passos || [] };
}

function novoForm() {
  return {
    cnpj: "",
    apelido: "",
    contatos: { ti: { ...CONTATO_VAZIO }, logistica: { ...CONTATO_VAZIO }, cotacao: { ...CONTATO_VAZIO } },
  };
}

export default function AdicionarTransportadoraModal({ isOpen, embarcadorId, onClose, onAdicionada }) {
  const { addToast } = useToast();
  const [form, setForm] = useState(novoForm);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setForm(novoForm());
    setErro("");
  }, [isOpen]);

  const setCampo = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  const setContato = (papel, campo, valor) =>
    setForm((prev) => ({ ...prev, contatos: { ...prev.contatos, [papel]: { ...prev.contatos[papel], [campo]: valor } } }));

  const cnpjDigits = digitsOnly(form.cnpj);
  const cnpjOk = cnpjDigits.length === 14 && isValidCnpj(cnpjDigits);
  const ti = form.contatos.ti;
  const tiOk = ti.nome.trim().length >= 2 && EMAIL_RE.test(ti.email.trim());
  const opcionaisOk = ["logistica", "cotacao"].every((k) => {
    const c = form.contatos[k];
    const preenchido = c.nome.trim() || c.email.trim() || c.telefone.trim();
    return !preenchido || EMAIL_RE.test(c.email.trim());
  });
  const podeEnviar = cnpjOk && tiOk && opcionaisOk && !enviando;

  const contatosParaEnvio = () => {
    const saida = {};
    PAPEIS.forEach(({ key }) => {
      const c = form.contatos[key];
      const nome = c.nome.trim();
      const email = c.email.trim();
      const telefone = c.telefone.trim();
      if (!nome && !email && !telefone) return;
      saida[key] = { nome, email, telefone: telefone || undefined };
    });
    return saida;
  };

  const enviar = async (event) => {
    if (event) event.preventDefault();
    if (!cnpjOk) {
      setErro(MENSAGENS.cnpj_invalido);
      return;
    }
    if (!tiOk) {
      setErro(MENSAGENS.contato_ti_obrigatorio);
      return;
    }
    setErro("");
    setEnviando(true);
    try {
      const { status, data } = await adicionarMinhaTransportadora({
        embarcadorId,
        cnpj: cnpjDigits,
        apelido: form.apelido.trim() || undefined,
        contatos: contatosParaEnvio(),
      });
      const r = lerResultado(status, data);
      if (r.fase === "pedido") {
        addToast({
          place: "tr",
          type: "success",
          title: "Pedido enviado à Everlog",
          description: "A Everlog inicia o onboarding da transportadora e você acompanha a fase na coluna Esteira.",
        });
      } else if (r.fase === "curadoria") {
        addToast({
          place: "tr",
          type: "warning",
          title: "Transportadora aguardando a Everlog",
          description: (r.curadoria && r.curadoria.motivo) || "A matriz desta transportadora precisa de ajuste. A Everlog libera e o onboarding segue.",
        });
      } else {
        const rotulo = FASE_ROTULO[r.estado] || r.estado || "Em onboarding";
        addToast({
          place: "tr",
          type: "success",
          title: `Transportadora adicionada: ${rotulo}`,
          description:
            r.estado === "AGUARDANDO_HOMOLOGACAO" || r.estado === "EM_CONFIGURACAO"
              ? "Transportadora nova na Rede: a Everlog confere o cadastro e libera o onboarding."
              : r.estado === "OPERACIONAL"
              ? "Pastas, usuário SFTP e portal prontos."
              : "Pastas, usuário SFTP e acesso ao portal estão sendo montados.",
        });
      }
      if (onAdicionada) onAdicionada(r);
      onClose();
    } catch (e) {
      const data = (e && e.response && e.response.data) || {};
      const msg = MENSAGENS[data.code] || data.error || "Falha ao adicionar a transportadora.";
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Não foi possível adicionar", description: msg });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={enviando ? undefined : onClose} modalClassName="modal-align-top" fade={false} size="lg">
      <Form onSubmit={enviar}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Adicionar transportadora</div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", marginBottom: "1rem" }}>
            Informe qualquer CNPJ da transportadora: o vínculo é sempre com a matriz (0001). Se ela ainda não está na Rede Everlog, é cadastrada pela Receita. A Everlog cuida da homologação e do onboarding (pastas, usuário SFTP e portal); você acompanha na coluna Esteira.
          </div>

          {erro ? (
            <div
              style={{
                background: AUTOPILOT_COLORS.statusDangerBg,
                border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
                borderRadius: 8,
                padding: "0.6rem 0.8rem",
                color: AUTOPILOT_COLORS.statusDanger,
                fontSize: "0.84rem",
                marginBottom: "0.9rem",
              }}
            >
              {erro}
            </div>
          ) : null}

          <Row>
            <Col md={5}>
              <FormGroup>
                <Label style={labelStyle}>CNPJ *</Label>
                <Input
                  value={form.cnpj}
                  onChange={(e) => { const v = mascaraDocumentoParcial(e.target.value); setCampo("cnpj", v); }}
                  placeholder="00.000.000/0000-00"
                  inputMode="numeric"
                  autoFocus
                  invalid={cnpjDigits.length === 14 && !cnpjOk}
                />
                {cnpjDigits.length === 14 && !cnpjOk ? <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusDanger }}>CNPJ inválido.</div> : null}
              </FormGroup>
            </Col>
            <Col md={7}>
              <FormGroup>
                <Label style={labelStyle}>Apelido</Label>
                <Input value={form.apelido} onChange={(e) => { const v = e.target.value; setCampo("apelido", v); }} placeholder='Ex.: "União Transportes"' maxLength={100} />
                <div style={hintStyle}>Opcional. Nome curto pelo qual a transportadora aparece; vira o nome da pasta no SFTP quando ela é nova na Rede.</div>
              </FormGroup>
            </Col>
          </Row>

          <Label style={{ ...labelStyle, fontWeight: 600, fontSize: "0.86rem", marginTop: "0.4rem" }}>Contatos da transportadora</Label>
          <div style={{ ...hintStyle, marginTop: 0, marginBottom: "0.5rem" }}>TI é obrigatório (nome e e-mail). Logística e Cotação são opcionais.</div>
          {PAPEIS.map((papel) => (
            <Row key={papel.key} form>
              <Col md={3} className="d-flex align-items-center">
                <span style={{ ...smallLabel, color: papel.obrigatorio ? AUTOPILOT_COLORS.text : AUTOPILOT_COLORS.muted }}>
                  {papel.label}
                  {papel.obrigatorio ? " *" : ""}
                </span>
              </Col>
              <Col md={3}>
                <FormGroup className="mb-2">
                  <Input bsSize="sm" placeholder="Nome" value={form.contatos[papel.key].nome} onChange={(e) => { const v = e.target.value; setContato(papel.key, "nome", v); }} maxLength={60} />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-2">
                  <Input bsSize="sm" type="email" placeholder="E-mail" value={form.contatos[papel.key].email} onChange={(e) => { const v = e.target.value; setContato(papel.key, "email", v); }} maxLength={100} />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Input
                    bsSize="sm"
                    placeholder="Telefone"
                    value={form.contatos[papel.key].telefone}
                    onChange={(e) => { const v = e.target.value.replace(/[^\d() +-]/g, "").slice(0, 20); setContato(papel.key, "telefone", v); }}
                  />
                </FormGroup>
              </Col>
            </Row>
          ))}
          {!tiOk && (ti.nome || ti.email) ? <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusWarning }}>Informe nome e um e-mail válido para o contato de TI.</div> : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={!podeEnviar}>
            {enviando ? <Spinner size="sm" /> : "Adicionar"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
